import { Folder, FolderShield } from "@untitledui/icons";
import type { Container } from "@/types/container";
import type { CustomFolder } from "@/types/documents";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { cx } from "@/utils/cx";
import { useTranslation } from "react-i18next";
import { documentService } from "@/data/mock-documents";

/**
 * Format date for display (dd/mm/yyyy HH:MM)
 */
const formatDate = (date: Date | string | undefined) => {
    if (!date) return "-";
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, "0");
    const minutes = d.getMinutes().toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

interface FolderCardProps {
    type: "container" | "custom_folder";
    container?: Container;
    customFolder?: CustomFolder;
    onClick?: () => void;
    onContextMenu?: (e: React.MouseEvent) => void;
    isSelected?: boolean;
    viewMode?: "card" | "list";
    /** When true, renders folder in document-style columns (for mixed folder+document lists) */
    useMixedLayout?: boolean;
    className?: string;
    documentCount?: number; // Optional: if provided, use this instead of calculating from mock service
    updatedAt?: Date | string; // Optional: if provided, use this for the last modified date
    createdAt?: Date | string; // Optional: creation date for mixed layout
    showCheckbox?: boolean;
    onCheckboxChange?: (checked: boolean) => void;
    /** Container shipment status for display in list view */
    containerStatus?: string | null;
}

export const FolderCard = ({
    type,
    container,
    customFolder,
    onClick,
    onContextMenu,
    isSelected = false,
    viewMode = "card",
    useMixedLayout = false,
    className,
    documentCount: providedDocumentCount,
    updatedAt: providedUpdatedAt,
    createdAt: providedCreatedAt,
    showCheckbox = false,
    onCheckboxChange,
    containerStatus,
}: FolderCardProps) => {
    const { t } = useTranslation("pages:documents");

    const name = type === "container" ? container?.containerNumber : customFolder?.name;
    const documentCount = providedDocumentCount !== undefined
        ? providedDocumentCount
        : (type === "container"
            ? documentService.getByContainerId(container?.id || "").length
            : customFolder?.containerIds.reduce((acc, containerId) => {
                return acc + documentService.getByContainerId(containerId).length;
            }, 0) || 0);

    // Format document count with translation
    const documentsCountText = t("drive.folder.documentsCount", { count: documentCount });

    // Get updated at from props or from entity
    const updatedAt = providedUpdatedAt
        || (type === "container" ? container?.updatedAt : customFolder?.updatedAt);

    // Get created at from props or from entity
    const createdAt = providedCreatedAt
        || (type === "container" ? container?.createdAt : customFolder?.createdAt);

    // Get folder type label for mixed layout
    const folderTypeLabel = type === "container"
        ? t("drive.folder.container")
        : t("drive.folder.customFolder");

    // Mixed layout: Render folder with document-style columns (Category, Status, Source, Uploaded, Modified)
    if (viewMode === "list" && useMixedLayout) {
        return (
            <div
                className={cx(
                    "flex items-center gap-3 rounded-lg px-4 py-3 cursor-pointer transition-all",
                    "hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2",
                    isSelected && "bg-active ring-2 ring-brand",
                    className
                )}
                onClick={onClick}
                onContextMenu={onContextMenu}
                role="button"
                tabIndex={0}
            >
                {/* Checkbox */}
                {showCheckbox && (
                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            isSelected={isSelected}
                            onChange={(checked) => onCheckboxChange?.(checked)}
                            size="sm"
                        />
                    </div>
                )}

                {/* Icon + Name (matching document-card layout) */}
                {type === "container" ? <FolderShield className="size-10 text-brand-600" /> : <Folder className="size-10 text-brand-600" />}
                <div className="flex-1 min-w-0 min-w-[200px]">
                    <p className="text-sm font-medium text-primary truncate">{name}</p>
                </div>

                {/* Category - shows folder type */}
                <div className="w-[140px] shrink-0">
                    <span className="text-xs text-secondary capitalize px-2 py-1 bg-secondary rounded-md">
                        {folderTypeLabel}
                    </span>
                </div>

                {/* Status - empty for folders */}
                <div className="w-[120px] shrink-0">
                    {/* Empty for folders */}
                </div>

                {/* Source - empty for folders */}
                <div className="w-[80px] shrink-0">
                    {/* Empty for folders */}
                </div>

                {/* Uploaded - creation date */}
                <div className="w-[130px] shrink-0 text-xs text-tertiary">
                    {formatDate(createdAt)}
                </div>

                {/* Last Modified - last modification inside folder */}
                <div className="w-[130px] shrink-0 text-xs text-tertiary">
                    {formatDate(updatedAt)}
                </div>

                {/* Actions - empty placeholder to match document layout */}
                <div className="w-[40px] shrink-0">
                    {/* Empty for folders - actions in context menu */}
                </div>
            </div>
        );
    }

    // Standard list layout: folder-specific columns (Name, Documents, Modified)
    if (viewMode === "list") {
        return (
            <div
                className={cx(
                    "flex items-center gap-3 rounded-lg px-4 py-3 cursor-pointer transition-all",
                    "hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2",
                    isSelected && "bg-active ring-2 ring-brand",
                    className
                )}
                onClick={onClick}
                onContextMenu={onContextMenu}
                role="button"
                tabIndex={0}
            >
                {/* Checkbox */}
                {showCheckbox && (
                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            isSelected={isSelected}
                            onChange={(checked) => onCheckboxChange?.(checked)}
                            size="sm"
                        />
                    </div>
                )}
                {type === "container" ? <FolderShield className="size-6 text-brand-600" /> : <Folder className="size-6 text-brand-600" />}
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary truncate">{name}</p>
                    <p className="text-xs text-tertiary">
                        {folderTypeLabel}
                    </p>
                </div>
                {/* Status */}
                <div className="w-[100px] shrink-0 text-xs">
                    {type === "container" && containerStatus ? (
                        <span className={cx(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                            containerStatus === "In Transit" && "bg-brand-50 text-brand-700",
                            containerStatus === "Booked" && "bg-blue-50 text-blue-700",
                            containerStatus === "Draft" && "bg-gray-100 text-gray-600",
                            containerStatus === "Arrived" && "bg-success-50 text-success-700",
                            containerStatus === "Delivered" && "bg-success-50 text-success-700",
                            containerStatus === "On Hold" && "bg-warning-50 text-warning-700",
                            containerStatus === "Cancelled" && "bg-error-50 text-error-700",
                        )}>
                            {containerStatus}
                        </span>
                    ) : (
                        <span className="text-quaternary">—</span>
                    )}
                </div>
                {/* Document Count */}
                <div className="w-[100px] shrink-0 text-xs text-tertiary">
                    {documentsCountText}
                </div>
                {/* Last Modified */}
                <div className="w-[130px] shrink-0 text-xs text-tertiary">
                    {formatDate(updatedAt)}
                </div>
            </div>
        );
    }

    return (
        <div
            className={cx(
                "relative rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary cursor-pointer transition-all",
                "hover:ring-2 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2",
                isSelected && "ring-2 ring-brand shadow-md",
                className
            )}
            onClick={onClick}
            onContextMenu={onContextMenu}
            role="button"
            tabIndex={0}
        >
            {/* Checkbox */}
            {showCheckbox && (
                <div className="absolute top-2 left-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        isSelected={isSelected}
                        onChange={(checked) => onCheckboxChange?.(checked)}
                        size="sm"
                    />
                </div>
            )}
            <div className="flex flex-col items-center text-center gap-3">
                {type === "container" ? <FolderShield className="size-8 text-brand-600" /> : <Folder className="size-8 text-brand-600" />}
                <div className="w-full min-w-0">
                    <p className="text-sm font-medium text-primary truncate" title={name}>
                        {name}
                    </p>
                    <p className="text-xs text-tertiary mt-1">
                        {documentsCountText}
                    </p>
                </div>
            </div>
        </div>
    );
};

